import type { CalendarBooking, FreeSegment, BandInfo, CellState } from './types';
import { getDayPrice, getFreeSegments, toYmd, dateRange } from './pricing';

// Resolve cell state for a given unit + date (server-authoritative via booking list)
export function getCellState(
    unitId: number,
    dateYmd: string,
    todayYmd: string,
    bookings: CalendarBooking[]
): CellState {
    if (dateYmd < todayYmd) return 'past';
    for (const b of bookings) {
        if (b.unitId !== unitId) continue;
        if (dateYmd >= b.from && dateYmd < b.to) return b.status as CellState;
    }
    if (getDayPrice(unitId, dateYmd) == null) return 'no-price';
    return 'free';
}

// Build booking band positions within the visible day window
export function buildBandInfos(
    unitId: number,
    days: Date[],
    bookings: CalendarBooking[]
): BandInfo[] {
    const fromYmd = toYmd(days[0]);
    const toYmd_ = toYmd(days[days.length - 1]);
    return bookings
        .filter(b => b.unitId === unitId && b.to > fromYmd && b.from <= toYmd_)
        .map(b => {
            const startIdx = Math.max(0, days.findIndex(d => toYmd(d) >= b.from));
            const endIdxRaw = days.findIndex(d => toYmd(d) >= b.to);
            const endIdx = endIdxRaw === -1 ? days.length : endIdxRaw;
            return { booking: b, startIdx, endIdx };
        });
}

// Build free-segment bands within the visible day window
export function buildFreeSegments(
    unitId: number,
    days: Date[],
    windowFrom: string,
    windowTo: string
): FreeSegment[] {
    const segs = getFreeSegments(unitId, windowFrom, windowTo);
    return segs.map(s => {
        const startIdx = days.findIndex(d => toYmd(d) === s.from);
        const endIdxRaw = days.findIndex(d => toYmd(d) === s.to);
        return {
            ...s,
            startIdx: startIdx < 0 ? 0 : startIdx,
            endIdx: (endIdxRaw < 0 ? days.length - 1 : endIdxRaw) + 1, // exclusive
        };
    }).filter(s => s.endIdx > s.startIdx && s.perNight != null);
}

// Compute price-tier (1–5) for heat-map coloring
export function priceTier(price: number, min: number, max: number): number {
    if (min === max) return 0;
    const r = (price - min) / (max - min);
    if (r < 0.2) return 1;
    if (r < 0.4) return 2;
    if (r < 0.6) return 3;
    if (r < 0.8) return 4;
    return 5;
}
